import { Reorder } from 'framer-motion';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useChangeTargetsOrderMutation } from 'api';
import { ReactComponent as AddIcon } from 'assets/icons/addIcon.svg';
import { Spinner } from 'components';
import { CreateButton } from 'features/documentCategories/components/DocumentCategories/styled';
import { useAuth } from 'hooks';
import { Links } from 'settings';
import { theme } from 'styles';
import { Target } from 'types';

import { EmptyStateKpi, TargetItem } from '../../components';
import { ContentW, HeaderW, ListItemW, ListW, Title } from './styled';

interface TargetsListProps {
  list: Target[];
  title: string;
  isActiveList?: boolean;
}

export function TargetsList({ list, title, isActiveList }: TargetsListProps): ReactElement {
  const { isManager } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [targetsList, setTargetsList] = useState(list);
  const [isDragging, setIsDragging] = useState(false);

  const { changeTargetsOrdersMutation, isLoading } = useChangeTargetsOrderMutation();

  async function handleReorder() {
    const targetsIds = targetsList.map((target) => target.id);
    await changeTargetsOrdersMutation({ targetsIds });
  }

  const listNode = (
    <ListW>
      {isActiveList && isManager ? (
        <>
          {targetsList.map((target) => (
            <ListItemW key={target.id}>
              <Reorder.Item
                value={target}
                as="div"
                drag
                onDragStart={() => {
                  setIsDragging(true);
                }}
                onDragEnd={() => {
                  setTimeout(() => {
                    setIsDragging(false);
                  }, 150);
                  handleReorder();
                }}
              >
                <TargetItem {...target} isDragging={isDragging} />
              </Reorder.Item>
            </ListItemW>
          ))}
        </>
      ) : (
        <>
          {targetsList.map((target) => (
            <ListItemW key={target.id}>
              <TargetItem {...target} />
            </ListItemW>
          ))}
        </>
      )}
    </ListW>
  );

  return (
    <ContentW>
      <HeaderW>
        <Title>{title}</Title>

        {isManager && isActiveList && (
          <CreateButton
            startIcon={<AddIcon style={{ fill: theme.palette.common.white }} />}
            onClick={() => navigate(Links.we.kpi.createTarget)}
            style={{
              height: '56px',
              padding: '16px 32px',
              backgroundColor: theme.palette.info.main,
              color: theme.palette.common.white,
              fontSize: '14px',
              fontWeight: 400,
              borderRadius: '8px',
              textTransform: 'none',
            }}
          >
            {t('kpi.add')}
          </CreateButton>
        )}
      </HeaderW>
      <Reorder.Group as="div" style={{ width: '100%' }} values={list} onReorder={setTargetsList}>
        {isLoading && <Spinner />}
        {list.length === 0 ? <EmptyStateKpi type="targets" /> : listNode}
      </Reorder.Group>
    </ContentW>
  );
}
